import { APP } from 'src/Configs/App'
import { rtkApi } from 'src/Services/rtkApi'
import {
  CampaignStatusEnum,
  CampaignTypeEnum,
  SpotifyCampaignAiUsageEnum,
  SpotifyCampaignMatchingType,
} from 'src/Types/campaigns'

export interface CreateSpotifyCampaignBody {
  artistName: string
  artistSpotifyId: string
  artistUrl: string
  trackImage: string
  trackLength: number
  trackName: string
  trackSpotifyId: string
  trackUrl: string
}

export interface UpdatePreReleaseTrackLinkBody {
  id: string
  artistSpotifyIds: string[]
  trackImage: string
  trackLength: number
  trackName: string
  trackSpotifyId: string
  trackUrl: string
}

export interface CreatePreReleaseSpotifyCampaignBody {
  artistName: string
  artistSpotifyId: string
  artistUrl: string
  trackName: string
}

export interface UpdateSpotifyCampaignReleaseDateBody {
  id: string
  releaseDate: string
}

export interface CreateSpotifyCampaignResponse {
  id: number
  status: CampaignStatusEnum
  type: CampaignTypeEnum
  startDate: string | null
  endDate: string | null
  track: {
    id: number
    spotifyId: string | null
    artistName: string
    trackName: string
    trackImage: string
  }
}

const spotifyCampaignApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    createSpotifyCampaign: builder.mutation<
      CreateSpotifyCampaignResponse,
      CreateSpotifyCampaignBody
    >({
      query: (body) => ({
        url: `${APP.TIKTOK_SERVER}/artist/spotify-campaigns`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SpotifyCampaign'],
    }),
    createPreReleaseSpotifyCampaign: builder.mutation<
      CreateSpotifyCampaignResponse,
      CreatePreReleaseSpotifyCampaignBody
    >({
      query: (body) => ({
        url: `${APP.TIKTOK_SERVER}/artist/spotify-campaigns/pre-release`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SpotifyCampaign'],
    }),
    updateSpotifyCampaignGenres: builder.mutation<
      void,
      { id: string; genreIds: number[] }
    >({
      query: ({ id, genreIds }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/spotify-campaigns/${id}/genres`,
        method: 'PUT',
        body: { genreIds },
      }),
      invalidatesTags: [
        'SpotifyCampaign',
        'SpotifyCampaignDetails',
        'MatchedCurators',
      ],
    }),
    updateSpotifyCampaignLanguages: builder.mutation<
      void,
      { id: string; languageIds: number[] }
    >({
      query: ({ id, languageIds }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/spotify-campaigns/${id}/languages`,
        method: 'PUT',
        body: { languageIds },
      }),
      invalidatesTags: [
        'SpotifyCampaign',
        'SpotifyCampaignDetails',
        'MatchedCurators',
      ],
    }),
    updateSpotifyCampaignMatchingType: builder.mutation<
      void,
      { id: string; matchingType: SpotifyCampaignMatchingType }
    >({
      query: ({ id, matchingType }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/spotify-campaigns/${id}/scheduling-details`,
        method: 'PATCH',
        body: { matchingType },
      }),
      invalidatesTags: ['SpotifyCampaign', 'SpotifyCampaignDetails'],
    }),
    updateSpotifyCampaignStartDate: builder.mutation<
      void,
      { id: string; startDate: string }
    >({
      query: ({ id, startDate }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/spotify-campaigns/${id}/scheduling-details`,
        method: 'PATCH',
        body: { startDate },
      }),
    }),
    updateSpotifyCampaignAiUsage: builder.mutation<
      void,
      { id: string; aiUsage: SpotifyCampaignAiUsageEnum }
    >({
      query: ({ id, aiUsage }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/spotify-campaigns/${id}/scheduling-details`,
        method: 'PATCH',
        body: { aiUsage },
      }),
      invalidatesTags: ['SpotifyCampaign', 'SpotifyCampaignDetails'],
    }),
    updateSpotifyCampaignReleaseDate: builder.mutation<
      void,
      UpdateSpotifyCampaignReleaseDateBody
    >({
      query: ({ id, releaseDate }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/spotify-campaigns/${id}/scheduling-details`,
        method: 'PATCH',
        body: { releaseDate },
      }),
      invalidatesTags: ['SpotifyCampaign', 'SpotifyCampaignDetails'],
    }),
    updateSpotifyCampaignTrackNote: builder.mutation<
      void,
      { id: string; trackNote: string | null }
    >({
      query: ({ id, trackNote }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/spotify-campaigns/${id}/scheduling-details`,
        method: 'PATCH',
        body: { trackNote },
      }),
      invalidatesTags: ['SpotifyCampaign', 'SpotifyCampaignDetails'],
    }),
    updatePreReleaseTrackLink: builder.mutation<
      void,
      UpdatePreReleaseTrackLinkBody
    >({
      query: ({ id, ...body }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/spotify-campaigns/${id}/pre-release-track-link`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['SpotifyCampaign', 'SpotifyCampaignDetails'],
    }),
  }),
})

export const {
  useCreateSpotifyCampaignMutation,
  useCreatePreReleaseSpotifyCampaignMutation,
  useUpdateSpotifyCampaignGenresMutation,
  useUpdateSpotifyCampaignLanguagesMutation,
  useUpdateSpotifyCampaignMatchingTypeMutation,
  useUpdateSpotifyCampaignStartDateMutation,
  useUpdateSpotifyCampaignTrackNoteMutation,
  useUpdateSpotifyCampaignAiUsageMutation,
  useUpdatePreReleaseTrackLinkMutation,
  useUpdateSpotifyCampaignReleaseDateMutation,
} = spotifyCampaignApi
